export const CATEGORIES = {
  custom:   { label: 'Proprietary',         color: '#FF4D6A', bg: 'rgba(255,77,106,0.1)',   border: 'rgba(255,77,106,0.25)',   icon: '🎯' },
  trend:    { label: 'Trend Following',      color: '#00D68F', bg: 'rgba(0,214,143,0.1)',    border: 'rgba(0,214,143,0.25)',    icon: '📈' },
  breakout: { label: 'Breakout',             color: '#3D8EF0', bg: 'rgba(61,142,240,0.1)',   border: 'rgba(61,142,240,0.25)',   icon: '💥' },
  reversal: { label: 'Reversal',             color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.25)',   icon: '🔄' },
  momentum: { label: 'Momentum',             color: '#A78BFA', bg: 'rgba(167,139,250,0.1)',  border: 'rgba(167,139,250,0.25)',  icon: '⚡' },
  candle:   { label: 'Candlestick Pattern',  color: '#2DD4BF', bg: 'rgba(45,212,191,0.1)',   border: 'rgba(45,212,191,0.25)',   icon: '🕯️' },
};

export const MARKETS = ['All', 'Stocks', 'Crypto', 'Forex', 'F&O'];
export const TIMEFRAMES = ['All', 'Scalping', 'Intraday', 'Swing', 'Positional'];
export const DIFFICULTIES = ['All', 'Beginner', 'Intermediate', 'Advanced'];

const strategies = [
  {
    id: 'cvc-setup',
    name: 'C+V+C Setup',
    subtitle: 'CPR + VWAP + Camarilla — your proprietary NSE F&O engine',
    category: 'custom',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Intraday',
    difficulty: 'Intermediate',
    winRate: null,
    rr: null,
    avgHold: '5 min',
    trades: 248,
    description:
      'A two-gate intraday signal engine for NSE F&O. Gate A fires when CPR is narrow/coiled with R3 breakout + VWAP confirmation. Gate B replaces CPR with momentum confirmation (≥2% change) for wide-CPR trending days. Three-layer exit: Hard SL → TP1 50% → TP2 9 EMA trail.',
    indicators: ['CPR (Central Pivot Range)', 'Camarilla R3–R5, S3–S5', 'VWAP', '9 EMA', 'ATR'],
    entryRules: [
      'Gate A (NARROW CPR): LTP breaks R3 + 0.1% buffer, LTP above VWAP, % change ≥ 0.3%, R:R ≥ 1.5',
      'Gate A (SEMI CPR): Same as NARROW but half position size (0.5×)',
      'Gate B (WIDE CPR): % change ≥ 2%, LTP cleanly above R3, LTP above VWAP, R:R ≥ 1.5',
      'VWAP fallback: if NSE VWAP = LTP (API bug), use CPR Pivot as proxy',
      'Enter on signal fire — next 5-min scan cycle',
    ],
    exitRules: [
      '50 MA crosses back below 200 MA',
      'Price closes below 200 MA on Daily chart',
      'Target achieved — trail remaining position',
    ],
    stopLoss: 'Hard SL = R4/S4 ± ATR × 0.5. Momentum (LTP > R4): SL = max(VWAP, R3) − ATR × 0.3',
    targets: ['TP1: Book 50% at Camarilla R4 (or R5 if LTP already past R4)', 'TP2: Trail remaining with 9 EMA → final target R5/S5', 'Min R:R = 1.5 to fire any signal'],
    mistakes: [
      'Trading WIDE CPR stocks without the 2% momentum confirmation',
      'Ignoring VWAP = LTP bug — always check VWAP source (NSE / CALC / PIVOT)',
      'Using full size on SEMI CPR signals — always halve the position',
      'Not trailing SL to 9 EMA after TP1 is hit',
    ],
    relatedIds: ['golden-crossover', 'volume-breakout', 'vwap-deviation'],
    featured: true,
    proprietary: true,
  },
  {
    id: 'golden-crossover',
    name: 'Golden Crossover',
    subtitle: '50 MA crosses above 200 MA — classic bull market signal',
    category: 'trend',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 71,
    rr: '1:2.8',
    avgHold: '18 days',
    trades: 248,
    description:
      'When the 50-period Moving Average crosses above the 200-period Moving Average, it signals a shift from bearish to bullish regime. The 50 MA represents medium-term momentum; the 200 MA represents long-term trend direction. Best used on Daily and Weekly charts for swing and positional entries.',
    indicators: ['50-period Simple MA', '200-period Simple MA', 'Volume'],
    entryRules: [
      '50 MA crosses above 200 MA on Daily/Weekly chart',
      'Both MAs should be sloping upward at crossover',
      'Volume on crossover candle above 20-day average',
      'Price above both MAs at time of entry',
      'Enter on close of crossover candle or next open',
    ],
    exitRules: [
      '50 MA crosses back below 200 MA (Death Cross)',
      'Price closes below 200 MA on Daily chart',
      'Bearish reversal candle pattern on Weekly chart',
      'Trailing stop triggered below 50 MA',
    ],
    stopLoss: 'Below the 200 MA at entry (2–3% below). Trail to 50 MA once 1R in profit.',
    targets: ['T1: 1:2 R:R — take 40–50% off position', 'T2: 1:3 R:R — take another 30% off', 'T3: Trail remaining 20–30% with 50 MA'],
    mistakes: [
      'Entering too late — 3–5 candles after the cross',
      'Ignoring volume at the crossover (low volume = weak signal)',
      'Using on timeframes under 1H (too many false signals)',
      'Not trailing stop loss — misses the big trend moves',
    ],
    relatedIds: ['death-cross', 'ema-ribbon', 'supertrend-breakout'],
    featured: true,
  },
  {
    id: 'death-cross',
    name: 'Death Cross',
    subtitle: '50 MA crosses below 200 MA — bearish regime shift',
    category: 'trend',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 66,
    rr: '1:2.4',
    avgHold: '14 days',
    trades: 198,
    description:
      'The bearish counterpart to the Golden Cross. When the 50 MA drops below the 200 MA, it confirms a shift from bullish to bearish regime. Most powerful when both MAs are declining and price is already trading below both.',
    indicators: ['50-period Simple MA', '200-period Simple MA', 'Volume'],
    entryRules: [
      '50 MA crosses below 200 MA on Daily/Weekly chart',
      'Both MAs declining at crossover',
      'Price trading below both MAs',
      'Volume confirmation on breakdown candle',
    ],
    exitRules: [
      '50 MA crosses back above 200 MA',
      'Price closes above 200 MA on Daily chart',
      'RSI reaches oversold and divergence appears',
    ],
    stopLoss: 'Above the 200 MA at entry. Trail to 50 MA once 1R in profit.',
    targets: ['T1: 1:2 R:R', 'T2: 1:3 R:R', 'T3: Trail with 50 MA'],
    mistakes: [
      'Shorting into strong support zones without confirmation',
      'Ignoring macro bullish tailwinds',
    ],
    relatedIds: ['golden-crossover', 'rsi-divergence', 'head-shoulders'],
  },
  {
    id: 'ema-ribbon',
    name: 'EMA Ribbon Strategy',
    subtitle: 'Multiple EMAs fanning out in trend direction',
    category: 'trend',
    markets: ['Forex', 'Crypto'],
    timeframe: 'Intraday',
    difficulty: 'Intermediate',
    winRate: 64,
    rr: '1:2.2',
    avgHold: '2 hours',
    trades: 312,
    description:
      'Multiple EMAs (8, 13, 21, 34, 55) fanning out in the direction of the trend create a visual momentum ribbon. When price rides above all EMAs and they are spread wide apart, momentum is strong. Pullbacks to the ribbon provide low-risk entries.',
    indicators: ['8 EMA', '13 EMA', '21 EMA', '34 EMA', '55 EMA'],
    entryRules: [
      'All 5 EMAs fanning out in same direction',
      'Price pulls back to touch the top EMA (8 or 13)',
      'Rejection candle at ribbon with volume',
      'Enter on close of rejection candle',
    ],
    exitRules: [
      'EMAs start converging (momentum slowing)',
      'Price closes below 55 EMA',
      'Target reached',
    ],
    stopLoss: 'Below the 55 EMA (bottom of the ribbon)',
    targets: ['T1: 1:2 R:R from ribbon touch', 'T2: Previous swing high/low'],
    mistakes: [
      'Entering when ribbon is flat — no trend, no trade',
      'Ignoring price action at the ribbon touch',
    ],
    relatedIds: ['golden-crossover', 'momentum-volume', 'nine-twenty-one-ema'],
  },
  {
    id: 'supertrend-breakout',
    name: 'Supertrend Breakout',
    subtitle: 'ATR-based dynamic support for trailing entries',
    category: 'trend',
    markets: ['Stocks', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 68,
    rr: '1:2.5',
    avgHold: '5 days',
    trades: 276,
    description:
      'Price closes above the Supertrend indicator line confirming a bullish trend shift. The Supertrend uses ATR to dynamically set support/resistance, making it adaptive to volatility. Best on Daily charts for swing trades.',
    indicators: ['Supertrend (7, 3)', 'ATR', 'Volume'],
    entryRules: [
      'Price closes above the Supertrend line (line turns green)',
      'Volume above 20-day average on crossover candle',
      'Enter on next candle open after close above',
      'RSI above 50 for confirmation',
    ],
    exitRules: [
      'Price closes below Supertrend (line turns red)',
      'Target achieved',
    ],
    stopLoss: 'At the Supertrend line value on entry day',
    targets: ['T1: 1:2.5 R:R', 'T2: Trail using the Supertrend line itself'],
    mistakes: [
      'Not waiting for candle close — using intrabar signals',
      'Using in choppy/sideways markets — generates whipsaws',
    ],
    relatedIds: ['golden-crossover', 'adx-trend', 'volume-breakout'],
  },
  {
    id: 'adx-trend',
    name: 'ADX Trend Strength',
    subtitle: 'ADX above 25 confirms a strong tradeable trend',
    category: 'trend',
    markets: ['Forex', 'Stocks'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 62,
    rr: '1:2.1',
    avgHold: '4 days',
    trades: 189,
    description:
      'The Average Directional Index (ADX) measures trend strength, not direction. ADX above 25 means the trend is strong enough to trade. Combined with +DI/-DI crossovers for precise entry timing in the direction of the trend.',
    indicators: ['ADX (14)', '+DI', '-DI'],
    entryRules: [
      'ADX above 25 and rising',
      '+DI crosses above -DI (bullish) or -DI crosses above +DI (bearish)',
      'Price in the direction of the crossover',
    ],
    exitRules: ['ADX drops below 20 (trend weakening)', '+DI/-DI crosses back'],
    stopLoss: 'Below the most recent swing low (long) or above swing high (short)',
    targets: ['T1: 1:2 R:R', 'T2: Hold while ADX stays above 25'],
    mistakes: [
      'Trading when ADX is below 20 — no trend, no trade',
      'Using ADX alone without +DI/-DI for direction',
    ],
    relatedIds: ['ema-ribbon', 'supertrend-breakout', 'momentum-volume'],
  },
  {
    id: 'volume-breakout',
    name: 'Volume Breakout',
    subtitle: 'Price breaks key level with 2x+ average volume',
    category: 'breakout',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Intraday',
    difficulty: 'Beginner',
    winRate: 67,
    rr: '1:2.5',
    avgHold: '1 day',
    trades: 412,
    description:
      'Price breaking a key support or resistance level accompanied by volume that is at least 2× the 20-day average. High volume confirms that the breakout has genuine institutional participation behind it — not a fakeout.',
    indicators: ['Volume (20-day MA)', 'Support/Resistance levels', 'ATR'],
    entryRules: [
      'Identify a key horizontal S/R level with at least 2 prior touches',
      'Price breaks the level with volume ≥ 2× 20-day average',
      'Breakout candle closes above resistance (or below support)',
      'Enter on the next candle open after breakout close',
    ],
    exitRules: [
      'Price closes back inside the broken level (failed breakout)',
      'Target reached',
      'Volume dries up — momentum fading',
    ],
    stopLoss: 'Below the breakout level (for longs) — the level should now act as support',
    targets: ['T1: ATR × 2 from breakout level', 'T2: Next major S/R level', 'T3: Measured move (height of consolidation)'],
    mistakes: [
      'Chasing breakouts — entering 3–5 candles after the break',
      'Low volume breakouts — usually fakeouts',
      'Not using the broken level as new support for SL',
    ],
    relatedIds: ['sr-breakout', 'orb', 'flag-pennant'],
    featured: true,
  },
  {
    id: 'sr-breakout',
    name: 'Support/Resistance Breakout',
    subtitle: 'Clean break above/below horizontal levels after consolidation',
    category: 'breakout',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 65,
    rr: '1:2.3',
    avgHold: '3 days',
    trades: 356,
    description:
      'Horizontal support and resistance levels represent areas where price has previously reversed. When price consolidates near these levels and then breaks out with conviction, the move tends to be strong and sustained.',
    indicators: ['Horizontal S/R', 'Volume', 'ATR'],
    entryRules: [
      'Draw horizontal S/R with at least 2 clear touches',
      'Price consolidates near the level for 3+ candles',
      'Breakout candle closes clearly above resistance',
      'Volume above average on breakout candle',
    ],
    exitRules: ['Price fails to hold above broken level', 'Target reached'],
    stopLoss: 'Below the consolidation range low (for breakout long)',
    targets: ['T1: Height of consolidation added to breakout point', 'T2: Next S/R level above'],
    mistakes: [
      'Drawing S/R lines through candle bodies instead of wicks',
      'Trading breakouts of weak levels (only 1 touch)',
    ],
    relatedIds: ['volume-breakout', 'flag-pennant', 'triangle-breakout'],
  },
  {
    id: 'orb',
    name: 'Opening Range Breakout',
    subtitle: 'Trade the breakout of the first 15–30 min candle range',
    category: 'breakout',
    markets: ['Stocks', 'F&O'],
    timeframe: 'Intraday',
    difficulty: 'Intermediate',
    winRate: 64,
    rr: '1:2.3',
    avgHold: '2 hours',
    trades: 521,
    description:
      'The first 15–30 minutes of the trading session establish the Opening Range — high and low. A breakout above the OR high or below the OR low with volume signals the likely direction of the day. Highly reliable on high-gap-up or gap-down days.',
    indicators: ['Opening Range (15/30 min)', 'Volume', 'VWAP', 'Previous Day High/Low'],
    entryRules: [
      'Mark the high and low of the first 15 (or 30) minute candle',
      'Wait for a clean close above OR high or below OR low',
      'Volume on breakout candle above session average',
      'Breakout in direction of pre-market gap if applicable',
    ],
    exitRules: ['Price returns inside the Opening Range', 'Target reached before market close'],
    stopLoss: 'Inside the Opening Range — below OR high for longs (or at OR midpoint)',
    targets: ['T1: OR height × 1 from breakout', 'T2: OR height × 2', 'T3: Previous day high/low'],
    mistakes: [
      'Trading ORB on flat open days (no gap, low volatility)',
      'Wide stops — SL should be tight to OR boundary',
      'Ignoring VWAP — breakout above OR and above VWAP is strongest',
    ],
    relatedIds: ['volume-breakout', 'cvc-setup', 'vwap-deviation'],
  },
  {
    id: 'flag-pennant',
    name: 'Flag & Pennant Breakout',
    subtitle: 'Consolidation after sharp move — measured move targets',
    category: 'breakout',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 69,
    rr: '1:2.7',
    avgHold: '4 days',
    trades: 287,
    description:
      'After a sharp directional move (the flagpole), price consolidates in a tight, parallel channel (flag) or converging triangle (pennant). The breakout from this consolidation targets a move equal to the flagpole height — a classic measured move.',
    indicators: ['Price channels', 'Volume', 'ATR'],
    entryRules: [
      'Identify a sharp flagpole move (strong trend)',
      'Price consolidates in a tight channel/triangle (3–15 candles)',
      'Volume contracts during consolidation',
      'Enter on breakout from the pattern in the trend direction',
      'Volume expands on breakout candle',
    ],
    exitRules: ['Price closes back inside the flag/pennant', 'Measured move target reached'],
    stopLoss: 'Below the lowest point of the flag/pennant',
    targets: ['T1: 50% of flagpole height from breakout', 'T2: 100% of flagpole height (full measured move)'],
    mistakes: [
      'Confusing a flag with a reversal pattern',
      'Entering before the breakout candle closes',
    ],
    relatedIds: ['sr-breakout', 'triangle-breakout', 'cup-handle'],
  },
  {
    id: 'triangle-breakout',
    name: 'Triangle Breakout',
    subtitle: 'Symmetrical, ascending, or descending triangle patterns',
    category: 'breakout',
    markets: ['Stocks', 'Crypto', 'Forex'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 63,
    rr: '1:2.2',
    avgHold: '5 days',
    trades: 241,
    description:
      'Triangles form as price makes lower highs and higher lows (symmetrical), lower highs with flat support (descending), or flat resistance with higher lows (ascending). The breakout from the apex signals the next directional move.',
    indicators: ['Trendlines', 'Volume', 'ATR'],
    entryRules: [
      'Draw converging trendlines connecting at least 2 highs and 2 lows',
      'Price breaks out of triangle with a candle close outside',
      'Volume expands on breakout',
      'Enter on breakout candle close or next open',
    ],
    exitRules: ['Price returns inside the triangle', 'Measured move target reached'],
    stopLoss: 'Below the breakout trendline',
    targets: ['Measured move: height of triangle at widest point, projected from breakout'],
    mistakes: [
      'Drawing sloppy trendlines — precision matters',
      'Trading very small triangles — measured move will be tiny',
    ],
    relatedIds: ['flag-pennant', 'sr-breakout', 'volume-breakout'],
  },
  {
    id: 'cup-handle',
    name: 'Cup & Handle Breakout',
    subtitle: 'Rounded cup with handle consolidation — powerful rally signal',
    category: 'breakout',
    markets: ['Stocks'],
    timeframe: 'Positional',
    difficulty: 'Intermediate',
    winRate: 72,
    rr: '1:3.1',
    avgHold: '30 days',
    trades: 134,
    description:
      'A rounded cup (U-shape) forms over weeks/months, followed by a small handle consolidation near the cup\'s rim. Breakout above the rim with volume signals a powerful continuation rally. One of the highest win-rate patterns in stocks.',
    indicators: ['Price structure', 'Volume', 'RS (Relative Strength)'],
    entryRules: [
      'Cup depth 20–35% from rim to base',
      'Cup forms over at least 7 weeks',
      'Handle forms in upper half of the cup (< 12% depth)',
      'Buy breakout above the rim/handle high on volume surge',
    ],
    exitRules: ['Price falls back into the handle', 'Measured move target reached'],
    stopLoss: 'Below the handle low (tight) or cup low (loose)',
    targets: ['Measured move: cup depth added to the rim breakout price'],
    mistakes: [
      'Buying a cup with a V-shape bottom instead of U-shape',
      'Trading without checking relative strength vs index',
    ],
    relatedIds: ['flag-pennant', '52-week-high', 'volume-breakout'],
  },
  {
    id: '52-week-high',
    name: '52-Week High Breakout',
    subtitle: 'Price breaking into new highs — no overhead resistance',
    category: 'breakout',
    markets: ['Stocks'],
    timeframe: 'Positional',
    difficulty: 'Beginner',
    winRate: 70,
    rr: '1:2.9',
    avgHold: '45 days',
    trades: 167,
    description:
      'Stocks making new 52-week highs are entering price discovery with zero overhead resistance. Combined with strong volume and relative strength vs the index, these can become multi-bagger moves. Buy the breakout, not the anticipation.',
    indicators: ['52-week high level', 'Volume', 'Relative Strength'],
    entryRules: [
      'Stock makes a new 52-week high or all-time high',
      'Volume on breakout day is 2× the 50-day average',
      'Stock has strong RS vs Nifty (outperforming the index)',
      'Enter within 2–3% of the 52-week high breakout',
    ],
    exitRules: ['Price falls 7–8% from buy point (standard loss limit)', 'Major market correction'],
    stopLoss: '7–8% below entry — non-negotiable',
    targets: ['20–25% gain as first target', 'Hold with trailing SL for bigger moves'],
    mistakes: [
      'Chasing extended stocks — only buy within 5% of the high',
      'Ignoring overall market conditions',
    ],
    relatedIds: ['cup-handle', 'volume-breakout', 'relative-strength'],
  },
  {
    id: 'double-top-bottom',
    name: 'Double Top / Double Bottom',
    subtitle: 'Two equal highs or lows — neckline break confirms reversal',
    category: 'reversal',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 66,
    rr: '1:2.4',
    avgHold: '6 days',
    trades: 298,
    description:
      'Double Top: two highs at approximately the same level, separated by a valley. Bearish reversal confirmed when price breaks below the neckline (the valley low). Double Bottom: the inverse pattern — two lows and bullish on neckline break.',
    indicators: ['Price structure', 'Volume', 'RSI'],
    entryRules: [
      'Identify two clear highs/lows at approximately the same price',
      'Draw the neckline at the valley high (double bottom) or valley low (double top)',
      'Enter on a candle close BELOW neckline (double top) or ABOVE neckline (double bottom)',
      'Volume increases on neckline breakout',
    ],
    exitRules: ['Price returns through neckline', 'Measured move target reached'],
    stopLoss: 'Above the second top (for double top short) or below second bottom (for long)',
    targets: ['Measured move: distance from neckline to the tops/bottoms, projected from neckline break'],
    mistakes: [
      'Entering before the neckline breaks — anticipation leads to failed trades',
      'The two tops/bottoms must be clear and well-separated',
    ],
    relatedIds: ['head-shoulders', 'rsi-divergence', 'bearish-engulfing'],
    featured: true,
  },
  {
    id: 'head-shoulders',
    name: 'Head & Shoulders',
    subtitle: 'Three-peak reversal — neckline break triggers measured move',
    category: 'reversal',
    markets: ['Stocks', 'Forex'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 68,
    rr: '1:2.6',
    avgHold: '10 days',
    trades: 187,
    description:
      'Three peaks where the middle (head) is highest, flanked by two lower peaks (shoulders). The neckline connects the two troughs. A close below the neckline with volume is the short trigger. Inverse H&S is the bullish counterpart.',
    indicators: ['Price structure', 'Volume', 'Neckline'],
    entryRules: [
      'Identify left shoulder, head (highest point), right shoulder',
      'Draw neckline connecting the two troughs',
      'Short entry: candle close below neckline',
      'Volume should increase on the neckline break',
      'Right shoulder should form at lower volume than left shoulder',
    ],
    exitRules: ['Price breaks back above neckline', 'Measured move target reached'],
    stopLoss: 'Above the right shoulder high',
    targets: ['Measured move: distance from head to neckline, projected downward from neckline break'],
    mistakes: [
      'Neckline must be drawn connecting the two troughs — not horizontal',
      'Asymmetric shoulders are fine — don\'t force perfection',
    ],
    relatedIds: ['double-top-bottom', 'rsi-divergence', 'macd-reversal'],
  },
  {
    id: 'rsi-divergence',
    name: 'RSI Divergence',
    subtitle: 'Price makes new high/low but RSI disagrees — early warning',
    category: 'reversal',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 63,
    rr: '1:2.2',
    avgHold: '5 days',
    trades: 334,
    description:
      'Bearish divergence: price makes a higher high but RSI makes a lower high — momentum is weakening even as price rises. Bullish divergence: price makes a lower low but RSI makes a higher low — selling pressure is exhausting. Context and location are critical.',
    indicators: ['RSI (14)', 'Price action', 'Support/Resistance'],
    entryRules: [
      'Price makes new high (bearish) or new low (bullish)',
      'RSI fails to make new high/low — divergence confirmed',
      'Divergence must occur at a key S/R level for maximum reliability',
      'Confirm with a reversal candle (pin bar, engulfing) before entry',
    ],
    exitRules: ['RSI returns to neutral zone', 'Target reached'],
    stopLoss: 'Above the highest high in the divergence (bearish) or below lowest low (bullish)',
    targets: ['T1: Previous S/R level', 'T2: Full retracement of the divergent move'],
    mistakes: [
      'Trading divergence alone without price confirmation candle',
      'Divergence can persist for many candles — don\'t fight the trend',
    ],
    relatedIds: ['macd-reversal', 'double-top-bottom', 'bollinger-reversal'],
    featured: true,
  },
  {
    id: 'macd-reversal',
    name: 'MACD Crossover Reversal',
    subtitle: 'MACD/signal crossover with histogram flip in oversold zone',
    category: 'reversal',
    markets: ['Forex', 'Stocks'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 61,
    rr: '1:2.0',
    avgHold: '5 days',
    trades: 276,
    description:
      'The MACD line crossing the signal line while in oversold territory, combined with the histogram flipping from negative to positive, signals a potential bullish reversal. Most reliable when the crossover occurs below the zero line.',
    indicators: ['MACD (12,26,9)', 'Signal Line', 'Histogram', 'RSI'],
    entryRules: [
      'MACD line crosses above signal line',
      'Histogram flips from negative to positive',
      'Crossover occurs below zero line (oversold) for bullish',
      'RSI below 40 for confirmation',
    ],
    exitRules: ['MACD crosses back below signal line', 'Target reached'],
    stopLoss: 'Below the recent swing low at time of entry',
    targets: ['T1: 1:2 R:R from entry', 'T2: Previous resistance level'],
    mistakes: [
      'Trading MACD crossovers in choppy markets — too many false signals',
      'Ignoring the zero line — crossovers above zero are weaker signals',
    ],
    relatedIds: ['rsi-divergence', 'bollinger-reversal', 'double-top-bottom'],
  },
  {
    id: 'bollinger-reversal',
    name: 'Bollinger Band Squeeze Reversal',
    subtitle: 'Price at outer band returns inside — mean reversion play',
    category: 'reversal',
    markets: ['Crypto', 'Forex'],
    timeframe: 'Intraday',
    difficulty: 'Intermediate',
    winRate: 62,
    rr: '1:2.1',
    avgHold: '2 hours',
    trades: 298,
    description:
      'When price touches or exceeds the outer Bollinger Band (2 standard deviations from the 20 MA) and then closes back inside, it signals a mean-reversion opportunity. The squeeze (bands contracting) before a move warns of explosive breakouts.',
    indicators: ['Bollinger Bands (20,2)', 'RSI', 'Volume'],
    entryRules: [
      'Price touches or closes outside the outer Bollinger Band',
      'Next candle closes back inside the band (reversal)',
      'RSI in overbought (>70) for short or oversold (<30) for long',
      'Enter on the candle that closes back inside',
    ],
    exitRules: ['Price reaches the middle band (20 MA)', 'Price touches opposite outer band'],
    stopLoss: 'Beyond the outer band extreme (the wick tip)',
    targets: ['T1: Middle band (20 MA)', 'T2: Opposite outer band'],
    mistakes: [
      'Shorting a band touch in a strong uptrend — the band can "walk"',
      'No volume confirmation on the reversal candle',
    ],
    relatedIds: ['rsi-divergence', 'vwap-deviation', 'doji'],
  },
  {
    id: 'vwap-deviation',
    name: 'Mean Reversion (VWAP)',
    subtitle: 'Price deviates from VWAP extremes and snaps back',
    category: 'reversal',
    markets: ['Stocks', 'F&O'],
    timeframe: 'Intraday',
    difficulty: 'Advanced',
    winRate: 65,
    rr: '1:2.3',
    avgHold: '45 min',
    trades: 489,
    description:
      'When price deviates 2–3 standard deviations from VWAP, it tends to snap back to VWAP as the "fair value" of the day. This intraday mean-reversion strategy is used by institutional desks and is particularly effective in liquid F&O stocks.',
    indicators: ['VWAP', 'VWAP Standard Deviation Bands', 'Volume'],
    entryRules: [
      'Price reaches 2nd or 3rd VWAP standard deviation band',
      'Reversal candle forms at the deviation level',
      'Volume dries up as price reaches extreme',
      'Enter as price starts moving back toward VWAP',
    ],
    exitRules: ['Price reaches VWAP (first target)', 'Price reaches opposite deviation band'],
    stopLoss: 'Beyond the deviation band extreme',
    targets: ['T1: VWAP itself', 'T2: Opposite 1st deviation band'],
    mistakes: [
      'Trading VWAP reversion on strong trend days — price may not revert',
      'VWAP resets daily — do not use across sessions',
    ],
    relatedIds: ['cvc-setup', 'orb', 'bollinger-reversal'],
  },
  {
    id: 'momentum-volume',
    name: 'Momentum + Volume Confluence',
    subtitle: 'Strong price momentum with above-average volume surge',
    category: 'momentum',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Intraday',
    difficulty: 'Intermediate',
    winRate: 67,
    rr: '1:2.4',
    avgHold: '1 day',
    trades: 378,
    description:
      'When strong directional price momentum aligns with a significant volume surge (2× or more average), it confirms genuine institutional participation. This combination filters out noise and identifies moves with the highest probability of continuation.',
    indicators: ['Volume (20-day MA)', 'Rate of Change (ROC)', 'ATR'],
    entryRules: [
      '% change ≥ 2% intraday in the first 30 minutes',
      'Volume ≥ 2× the 20-day average',
      'Price above VWAP (for longs)',
      'No major resistance within 2× ATR above entry',
    ],
    exitRules: ['Volume collapses — momentum exhausted', 'Target reached'],
    stopLoss: 'VWAP − ATR × 0.5 (for longs)',
    targets: ['T1: 2× ATR from entry', 'T2: Nearest major resistance'],
    mistakes: [
      'Chasing momentum stocks that have already moved 5–10%',
      'No stop — momentum can reverse violently',
    ],
    relatedIds: ['cvc-setup', 'volume-breakout', 'relative-strength'],
  },
  {
    id: 'relative-strength',
    name: 'Relative Strength (RS)',
    subtitle: 'Trade stocks outperforming the index — market leaders',
    category: 'momentum',
    markets: ['Stocks'],
    timeframe: 'Swing',
    difficulty: 'Advanced',
    winRate: 69,
    rr: '1:2.8',
    avgHold: '14 days',
    trades: 198,
    description:
      'Stocks that consistently outperform the Nifty/index — rising faster in uptrends and falling less in downtrends — are market leaders. Trading these stocks in the direction of the market trend gives a significant edge. RS is not RSI.',
    indicators: ['Relative Strength line vs Nifty', 'Volume', 'RS Rating'],
    entryRules: [
      'Stock\'s RS line making new highs (outperforming the index)',
      'Price near a proper base breakout or pullback entry',
      'Volume above average on up days, low on down days',
      'Overall market in confirmed uptrend',
    ],
    exitRules: ['RS line starts underperforming (stock lagging index)', 'Market enters correction'],
    stopLoss: '7–8% below buy point',
    targets: ['20–25% gain as minimum target', 'Hold leaders through normal pullbacks'],
    mistakes: [
      'Buying laggards hoping they catch up — they rarely do',
      'Trading RS stocks in a bear market',
    ],
    relatedIds: ['52-week-high', 'cup-handle', 'momentum-volume'],
  },
  {
    id: 'nine-twenty-one-ema',
    name: '9/21 EMA Cross',
    subtitle: 'Fast 9 EMA crosses slow 21 EMA — quick momentum signal',
    category: 'momentum',
    markets: ['Forex', 'Crypto'],
    timeframe: 'Scalping',
    difficulty: 'Beginner',
    winRate: 60,
    rr: '1:1.9',
    avgHold: '30 min',
    trades: 621,
    description:
      'The 9/21 EMA crossover is a fast momentum system ideal for crypto and forex scalping. The 9 EMA crossing above the 21 EMA signals bullish momentum; below signals bearish. Works best when combined with a higher-timeframe trend filter.',
    indicators: ['9 EMA', '21 EMA', 'Volume', 'Higher TF trend'],
    entryRules: [
      '9 EMA crosses above 21 EMA (bullish signal)',
      'Higher timeframe (1H or 4H) trend agrees',
      'Price above both EMAs after crossover',
      'Volume above average on crossover candle',
    ],
    exitRules: ['9 EMA crosses back below 21 EMA', 'Target reached'],
    stopLoss: 'Below the most recent swing low or below 21 EMA',
    targets: ['T1: 1:2 R:R', 'T2: Next resistance level'],
    mistakes: [
      'Trading this in ranging/choppy conditions — whipsaws constantly',
      'Ignoring the higher timeframe trend',
    ],
    relatedIds: ['ema-ribbon', 'momentum-volume', 'volume-breakout'],
  },
  {
    id: 'hammer',
    name: 'Hammer at Support',
    subtitle: 'Long lower wick at support — strong bullish reversal',
    category: 'candle',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 69,
    rr: '1:2.5',
    avgHold: '3 days',
    trades: 445,
    description:
      'A Hammer has a small body at the top of the range and a long lower wick (at least 2× the body) showing sellers pushed price down sharply but buyers stepped in and drove it back up before the close. At a key support level, this is a high-probability reversal signal.',
    indicators: ['Candlestick pattern', 'Support levels', 'Volume'],
    entryRules: [
      'Small body at the TOP of candle range (open ≈ close)',
      'Lower wick at least 2–3× the body length',
      'Little to no upper wick',
      'Forms at a key support zone, Fibonacci level, or MA',
      'Next candle closes above the Hammer high (confirmation)',
    ],
    exitRules: ['Price closes below the Hammer low', 'Target reached'],
    stopLoss: 'Below the low of the Hammer\'s wick',
    targets: ['T1: Next resistance level', 'T2: Previous swing high'],
    mistakes: [
      'Trading without confirmation candle — the next candle must close bullish',
      'Hammer at a random level — must be at a key support zone',
    ],
    relatedIds: ['bullish-engulfing', 'morning-star', 'pin-bar'],
  },
  {
    id: 'bullish-engulfing',
    name: 'Bullish Engulfing',
    subtitle: 'Large green candle swallows prior red — buying absorption',
    category: 'candle',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 69,
    rr: '1:2.1',
    avgHold: '3 days',
    trades: 512,
    description:
      'A large green candle that completely engulfs the body of the prior red candle. This shows an overwhelming wave of buyers absorbing all available selling pressure — a decisive power shift from bears to bulls.',
    indicators: ['Candlestick pattern', 'Support levels', 'Volume'],
    entryRules: [
      'Green candle opens below prior red candle\'s close',
      'Green candle closes above prior red candle\'s open (full body engulf)',
      'Green body is larger than red body',
      'Appears after a downtrend or at key support',
      'Higher volume on green candle = stronger signal',
    ],
    exitRules: ['Price closes below the engulfing candle low', 'Target reached'],
    stopLoss: 'Below the low of the engulfing candle',
    targets: ['T1: 1:2 R:R', 'T2: Next resistance level'],
    mistakes: [
      'Partial engulf — the green candle must completely engulf the red body',
      'No volume confirmation weakens the signal significantly',
    ],
    relatedIds: ['hammer', 'morning-star', 'three-white-soldiers'],
    featured: true,
  },
  {
    id: 'morning-star',
    name: 'Morning Star',
    subtitle: '3-candle bottom reversal: red, doji, large green',
    category: 'candle',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 71,
    rr: '1:2.4',
    avgHold: '4 days',
    trades: 267,
    description:
      'A powerful 3-candle bullish reversal. Candle 1: large bearish red. Candle 2: small doji/spinning top (indecision at the bottom). Candle 3: large bullish green closing back into Candle 1\'s body. Shows a clear battle won by buyers.',
    indicators: ['Candlestick pattern (3-candle)', 'Support levels', 'Volume'],
    entryRules: [
      'Candle 1: Large bearish red candle continuing the downtrend',
      'Candle 2: Small body doji/spinning top — indecision at the bottom',
      'Candle 3: Large bullish green closing above midpoint of Candle 1',
      'Volume increases on Candle 3',
    ],
    exitRules: ['Price closes below Candle 2 (doji) low', 'Target reached'],
    stopLoss: 'Below the low of Candle 2 (the doji)',
    targets: ['T1: Top of Candle 1 (full retracement)', 'T2: Next resistance above'],
    mistakes: [
      'Candle 2 must be small — a large candle breaks the pattern',
      'All three candles must be clearly distinct in location',
    ],
    relatedIds: ['bullish-engulfing', 'hammer', 'evening-star'],
  },
  {
    id: 'pin-bar',
    name: 'Pin Bar Rejection',
    subtitle: 'Long wick rejection at key level — decisive price rejection',
    category: 'candle',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 67,
    rr: '1:2.3',
    avgHold: '3 days',
    trades: 489,
    description:
      'A candle with a very small body at one end and an extremely long wick on the other — showing sharp price rejection at a key level. Bullish pin bars (long lower wick) reject lows. Bearish pin bars (long upper wick) reject highs. Works on all timeframes.',
    indicators: ['Candlestick pattern', 'Support/Resistance', 'Key levels'],
    entryRules: [
      'Wick is at least 2/3 of total candle length',
      'Small body at the opposite end of the wick',
      'Must form at a key S/R level, Fibonacci, or MA',
      'Enter on open of next candle (aggressive) or break of pin bar high/low (conservative)',
    ],
    exitRules: ['Price revisits the pin bar level', 'Target reached'],
    stopLoss: 'Just beyond the tip of the rejection wick',
    targets: ['T1: Next S/R level', 'T2: 2× the pin bar range from entry'],
    mistakes: [
      'Trading pin bars at random levels — the key level is everything',
      'Short wick relative to body — not a pin bar',
    ],
    relatedIds: ['hammer', 'shooting-star', 'bullish-engulfing'],
  },
  {
    id: 'inside-bar',
    name: 'Inside Bar Breakout',
    subtitle: 'Small candle within prior candle — coiling energy release',
    category: 'candle',
    markets: ['Stocks', 'Forex'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 64,
    rr: '1:2.0',
    avgHold: '3 days',
    trades: 312,
    description:
      'An Inside Bar\'s high and low are completely contained within the prior candle\'s range. The market is compressing energy. The breakout from the mother candle\'s range signals the direction of the next move — trade in the direction of the prevailing trend.',
    indicators: ['Candlestick pattern', 'Trend direction', 'S/R levels'],
    entryRules: [
      'Inside bar high is lower than mother candle high',
      'Inside bar low is higher than mother candle low',
      'Place buy stop above mother candle high (bullish) or sell stop below low (bearish)',
      'Trade in direction of prevailing trend for best results',
    ],
    exitRules: ['Price returns inside the mother candle range', 'Target reached'],
    stopLoss: 'Opposite end of the mother candle',
    targets: ['T1: 2× the mother candle range from entry', 'T2: Next S/R level'],
    mistakes: [
      'Trading inside bars in choppy markets — no trend = no edge',
      'Not using the mother candle boundaries for SL placement',
    ],
    relatedIds: ['pin-bar', 'flag-pennant', 'triangle-breakout'],
  },
  {
    id: 'shooting-star',
    name: 'Shooting Star',
    subtitle: 'Long upper wick at resistance — sellers reject highs',
    category: 'candle',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 65,
    rr: '1:2.1',
    avgHold: '3 days',
    trades: 398,
    description:
      'The bearish mirror of the Hammer. A small body at the bottom of the range with a very long upper wick — sellers aggressively pushed price back down from intraday highs, wiping out all gains. At a key resistance zone, this signals a high-probability reversal.',
    indicators: ['Candlestick pattern', 'Resistance levels', 'Volume'],
    entryRules: [
      'Small body at BOTTOM of candle range',
      'Upper wick at least 2–3× the body length',
      'Little to no lower wick',
      'Forms at a clear resistance zone after an uptrend',
      'Next candle closes below Shooting Star low (confirmation)',
    ],
    exitRules: ['Price closes above the Shooting Star high', 'Target reached'],
    stopLoss: 'Above the high of the Shooting Star\'s upper wick',
    targets: ['T1: Next support level', 'T2: Previous swing low'],
    mistakes: [
      'No confirmation candle — must wait for next candle to close bearish',
      'Trading at a weak resistance level with only 1 prior touch',
    ],
    relatedIds: ['bearish-engulfing', 'evening-star', 'pin-bar'],
  },
  {
    id: 'bearish-engulfing',
    name: 'Bearish Engulfing',
    subtitle: 'Large red candle swallows prior green — selling takeover',
    category: 'candle',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 67,
    rr: '1:2.2',
    avgHold: '3 days',
    trades: 478,
    description:
      'A large red candle that completely engulfs the body of the prior green candle. Sellers overwhelmed buyers in a single session, erasing all prior gains and signalling a decisive shift from bulls to bears.',
    indicators: ['Candlestick pattern', 'Resistance levels', 'Volume'],
    entryRules: [
      'Red candle opens above prior green candle\'s close',
      'Red candle closes below prior green candle\'s open (full body engulf)',
      'Red body is larger than green body',
      'Appears after an uptrend or at key resistance',
      'Higher volume on red candle = stronger signal',
    ],
    exitRules: ['Price closes above the engulfing candle high', 'Target reached'],
    stopLoss: 'Above the high of the bearish engulfing candle',
    targets: ['T1: 1:2 R:R', 'T2: Next support level below'],
    mistakes: [
      'Partial engulf only — red body must completely swallow the green body',
      'Trading this in a strong uptrend where dips are bought',
    ],
    relatedIds: ['shooting-star', 'evening-star', 'head-shoulders'],
  },
  {
    id: 'evening-star',
    name: 'Evening Star',
    subtitle: '3-candle top reversal: green, doji, large red',
    category: 'candle',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Intermediate',
    winRate: 69,
    rr: '1:2.3',
    avgHold: '4 days',
    trades: 234,
    description:
      'The bearish counterpart to the Morning Star. Candle 1: large bullish green. Candle 2: small doji/spinning top (indecision at the top). Candle 3: large bearish red closing back into Candle 1\'s body. Signals definitive exhaustion of the uptrend.',
    indicators: ['Candlestick pattern (3-candle)', 'Resistance levels', 'Volume'],
    entryRules: [
      'Candle 1: Large bullish green candle in uptrend',
      'Candle 2: Small body doji/spinning top — indecision at the top',
      'Candle 3: Large bearish red closing below midpoint of Candle 1',
      'Volume increases on Candle 3',
    ],
    exitRules: ['Price closes above Candle 2 high', 'Target reached'],
    stopLoss: 'Above the high of Candle 2 (the doji)',
    targets: ['T1: Bottom of Candle 1 (full retracement)', 'T2: Next support below'],
    mistakes: [
      'Candle 3 must close BELOW the midpoint of Candle 1 — partial is weak',
      'Trading without volume confirmation on Candle 3',
    ],
    relatedIds: ['bearish-engulfing', 'shooting-star', 'morning-star'],
  },
  {
    id: 'doji',
    name: 'Doji at Key Zone',
    subtitle: 'Cross candle showing indecision — context is everything',
    category: 'candle',
    markets: ['Stocks', 'Crypto', 'Forex', 'F&O'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 60,
    rr: '1:1.8',
    avgHold: '2 days',
    trades: 387,
    description:
      'A Doji forms when open and close are virtually equal, creating a cross shape. This represents a perfect standoff — neither side won. By itself the Doji is neutral. At support it is potentially bullish; at resistance it is potentially bearish. ALWAYS wait for the next candle to confirm direction.',
    indicators: ['Candlestick pattern', 'Support/Resistance', 'Volume'],
    entryRules: [
      'Open and close are virtually equal (tiny or no body)',
      'Must form at a key S/R level for significance',
      'NEVER trade the Doji itself — always wait for confirmation',
      'Bullish: next candle breaks above Doji high',
      'Bearish: next candle breaks below Doji low',
    ],
    exitRules: ['Price moves back through Doji range', 'Target reached'],
    stopLoss: 'Beyond the opposite extreme of the Doji (beyond the wick)',
    targets: ['T1: Next S/R level in direction of breakout'],
    mistakes: [
      'Trading a Doji alone without confirmation — the most common mistake',
      'Doji in the middle of a trend with no key level — not meaningful',
    ],
    relatedIds: ['morning-star', 'evening-star', 'inside-bar'],
  },
  {
    id: 'three-white-soldiers',
    name: 'Three White Soldiers',
    subtitle: '3 consecutive strong green candles — sustained momentum',
    category: 'candle',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 72,
    rr: '1:2.6',
    avgHold: '5 days',
    trades: 198,
    description:
      'Three consecutive long bullish candles, each opening within the prior candle\'s body and closing near its own high — a staircase of persistent buying. Shows organised, institutional accumulation with no meaningful retracement.',
    indicators: ['Candlestick pattern (3-candle)', 'Volume', 'Support levels'],
    entryRules: [
      'Three consecutive green/white candles',
      'Each candle opens within the prior candle\'s body',
      'Each candle closes near its high (small upper wick)',
      'Volume ideally increasing across the three candles',
      'Enter on close of the 3rd candle or brief pullback entry',
    ],
    exitRules: ['Price retraces significantly (>50% of 3-candle move)', 'Target reached'],
    stopLoss: 'Below the low of the first soldier candle',
    targets: ['T1: Equal move to the 3-candle range projected upward', 'T2: Next key resistance'],
    mistakes: [
      'Candles with very large upper wicks — soldiers losing their conviction',
      'Buying exhausted moves — three soldiers after a massive rally may be topping',
    ],
    relatedIds: ['bullish-engulfing', 'morning-star', 'three-black-crows'],
  },
  {
    id: 'three-black-crows',
    name: 'Three Black Crows',
    subtitle: '3 consecutive strong red candles — relentless selling',
    category: 'candle',
    markets: ['Stocks', 'Crypto'],
    timeframe: 'Swing',
    difficulty: 'Beginner',
    winRate: 70,
    rr: '1:2.4',
    avgHold: '4 days',
    trades: 187,
    description:
      'Three consecutive long bearish candles, each opening within the prior candle\'s body and closing near its own low. A descending staircase of relentless selling pressure — the bearish equivalent of Three White Soldiers.',
    indicators: ['Candlestick pattern (3-candle)', 'Volume', 'Resistance levels'],
    entryRules: [
      'Three consecutive red/black candles',
      'Each candle opens within prior candle\'s body',
      'Each candle closes near its low (small lower wick)',
      'Volume ideally increasing across the three candles',
      'Enter short on close of 3rd candle or pullback to 2nd candle midpoint',
    ],
    exitRules: ['Price retraces significantly (>50% of 3-candle move)', 'Target reached'],
    stopLoss: 'Above the high of the first crow candle',
    targets: ['T1: Equal move to the 3-candle range projected downward', 'T2: Next key support'],
    mistakes: [
      'Candles with very large lower wicks — crows weakening',
      'Shorting after a massive sell-off — exhaustion may be near',
    ],
    relatedIds: ['bearish-engulfing', 'evening-star', 'three-white-soldiers'],
  },
];

export default strategies;

export function getStrategyById(id) {
  return strategies.find(s => s.id === id) || null;
}

export function getRelatedStrategies(ids) {
  return ids.map(id => strategies.find(s => s.id === id)).filter(Boolean);
}

export function filterStrategies({ category = 'all', market = 'all', timeframe = 'all', difficulty = 'all', search = '' }) {
  return strategies.filter(s => {
    if (category !== 'all' && s.category !== category) return false;
    if (market !== 'all' && !s.markets.includes(market)) return false;
    if (timeframe !== 'all' && s.timeframe !== timeframe) return false;
    if (difficulty !== 'all' && s.difficulty !== difficulty) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !s.description.toLowerCase().includes(q)) return false;
    }
    return true;
  });
}
